import { CLOCK } from "@eldrun/content";
import { BIOME } from "@eldrun/worldgen";

/**
 * Leitos de ambiente (F46): que leito gravado toca, e quanto, pelo lugar, pela hora e pela Jangada.
 * Puro (testável em Node): pesos que somam 1, a mistura suavizada com histerese e no máximo 3
 * tocando, a pré-carga e o quanto cada camada sintetizada abaixa sob o leito que a cobre.
 */

export const BED_IDS = ["floresta-dia", "floresta-noite", "praia", "mar", "mar-tempestade"] as const;
export type BedId = (typeof BED_IDS)[number];
/** Os leitos e a parte sem leito (`nenhum`: só a síntese, como o vento das montanhas). */
export type Slot = BedId | "nenhum";
export const SLOTS: readonly Slot[] = [...BED_IDS, "nenhum"];
export type Weights = Record<Slot, number>;

export const AMBIENCE = {
  /** Na terra, a praia entra de 45 m até 10 m da linha da costa. */
  beachFrom: 45,
  beachFull: 10,
  /** No mar, da praia ao mar aberto em 60 m. */
  seaFull: 60,
  /** A noite entra nos 30 s da Boca da Noite e sai nos primeiros 20 s do dia. */
  dusk: CLOCK.dusk,
  dawn: 20,
  /** Suavização (constante de tempo, s): 1,5 s com o lugar, 4 s com a Jangada (a tempestade entrando ou saindo). */
  tau: 1.5,
  raftTau: 4,
  /** Histerese: pede o leito acima deste peso e o solta depois de 8 s com peso zero (e já calado). */
  request: 0.03,
  releaseMs: 8000,
  silent: 0.01,
  /** No máximo 3 tocando (o LRU do banco); quem já toca leva esta vantagem para não trocar à toa. */
  maxPlaying: 3,
  stickiness: 0.05,
  /** Pré-carga da praia e do mar com a costa a menos de 90 m. */
  prefetchCoast: 90,
} as const;

/** Biomas com os leitos de floresta (decisão do dono): floresta, charneca e pântano. */
const FOREST: ReadonlySet<number> = new Set([BIOME.mata, BIOME.cerrado, BIOME.mangue]);
/** Estágios da Jangada com a tempestade: a travessia e ancorada na luta com o Dragão. */
const STORM_STAGES: ReadonlySet<string> = new Set(["travessia", "ancorada"]);

export interface AmbienceInput {
  /** O ouvinte sobre a terra (o chão acima do nível do mar). */
  land: boolean;
  /** Distância até a linha da costa (m): na terra, até o mar; no mar, até a terra. */
  coast: number;
  /** 0 de dia, 1 de noite (`nightBlend`). */
  nightBlend: number;
  raftStage: string | undefined;
  /** Câmera embaixo d'água: nenhum leito (como o vento). */
  underwater: boolean;
  /** Bioma no ouvinte (`BIOME`). */
  biome: number;
  /** Boca da Noite: pré-carga do leito da noite. */
  dusk?: boolean;
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const storming = (i: AmbienceInput) => i.raftStage !== undefined && STORM_STAGES.has(i.raftStage);
/** Distância sem número (posição inválida): longe da costa. Um NaN nos pesos ficaria para sempre na suavização. */
const far = (coast: number) => (Number.isNaN(coast) ? Infinity : coast);
/** Quanto da praia na terra, pela distância da costa. */
const beachShare = (coast: number) => clamp01((AMBIENCE.beachFrom - far(coast)) / (AMBIENCE.beachFrom - AMBIENCE.beachFull));

export function emptyWeights(): Weights {
  return { "floresta-dia": 0, "floresta-noite": 0, praia: 0, mar: 0, "mar-tempestade": 0, nenhum: 0 };
}

/**
 * Pesos dos leitos (somam 1): embaixo d'água, nenhum; na travessia e ancorada, só a tempestade; no
 * mar, da praia ao mar aberto; na terra, a praia perto da costa e o resto na floresta (dia e noite
 * pelo `nightBlend`) nos biomas dela, ou sem leito (montanhas e praia longe da água).
 */
export function ambienceTargets(i: AmbienceInput): Weights {
  const w = emptyWeights();
  if (i.underwater) {
    w.nenhum = 1;
    return w;
  }
  if (storming(i)) {
    w["mar-tempestade"] = 1;
    return w;
  }
  if (!i.land) {
    w.mar = clamp01(far(i.coast) / AMBIENCE.seaFull);
    w.praia = 1 - w.mar;
    return w;
  }
  w.praia = beachShare(i.coast);
  const inland = 1 - w.praia;
  if (FOREST.has(i.biome)) {
    const n = Number.isNaN(i.nightBlend) ? 0 : clamp01(i.nightBlend);
    w["floresta-dia"] = inland * (1 - n);
    w["floresta-noite"] = inland * n;
  } else w.nenhum = inland;
  return w;
}

/** Vento das montanhas (0 a 1): a parte do interior sem leito na serra, onde o vento sintetizado sobe. */
export function mountainWind(i: AmbienceInput): number {
  if (!i.land || i.underwater || storming(i) || i.biome !== BIOME.serra) return 0;
  return 1 - beachShare(i.coast);
}

/** O relógio da partida como o cliente o lê (`ClockReading`). */
export interface ClockLike {
  day: number;
  night: boolean;
  elapsed: number;
  remaining: number;
  dusk: boolean;
}

/**
 * Noite (0 a 1) pelo relógio: sobe nos 30 s da Boca da Noite, fica em 1 de noite e desce nos
 * primeiros 20 s do dia (a partir do Dia 1: a partida começa de manhã).
 */
export function nightBlend(r: ClockLike | undefined): number {
  if (!r) return 0;
  if (r.night) return 1;
  if (r.dusk) return clamp01(1 - r.remaining / AMBIENCE.dusk);
  if (r.day > 0 && r.elapsed < AMBIENCE.dawn) return clamp01(1 - r.elapsed / AMBIENCE.dawn);
  return 0;
}

/** Leitos a baixar antes de precisar: a costa perto (praia e mar), a Boca da Noite e a contagem da Jangada. */
export function prefetchBeds(i: AmbienceInput): BedId[] {
  const out: BedId[] = [];
  if (i.coast < AMBIENCE.prefetchCoast) out.push("praia", "mar");
  if (i.dusk) out.push("floresta-noite");
  if (i.raftStage === "contagem") out.push("mar-tempestade");
  return out;
}

export interface AmbienceMix {
  input: AmbienceInput;
  targets: Weights;
  /** Pesos suavizados (o ganho de cada leito é a raiz: potência igual nas transições). */
  levels: Weights;
  /** Vento das montanhas, suavizado. */
  mountain: number;
  /** Leitos pedidos (histerese) e os que tocam (até 3, os de maior peso). */
  requested: BedId[];
  playing: BedId[];
  prefetch: BedId[];
}

/**
 * A mistura dos leitos a cada quadro: aproxima os pesos dos alvos (exponencial, independente da
 * taxa de quadros), pede um leito acima de 0,03 e só o solta depois de 8 s com peso zero e já
 * calado (sem vai e vem na borda da praia), toca no máximo 3 (um quarto tira o de menor peso) e
 * diz o que pré-carregar.
 */
export class AmbienceMixer {
  private readonly levels = emptyWeights();
  private mountain = 0;
  private last: number | undefined;
  /** Leito pedido → desde quando o alvo está em zero (ms), se está. */
  private readonly requested = new Map<BedId, number | undefined>();
  private playing: BedId[] = [];

  /** `now` em ms (o relógio da página). */
  step(i: AmbienceInput, now: number): AmbienceMix {
    const targets = ambienceTargets(i);
    const dt = this.last === undefined ? 0 : Math.max(0, (now - this.last) / 1000);
    this.last = now;
    const storm = targets["mar-tempestade"] > 0 || this.levels["mar-tempestade"] > AMBIENCE.silent;
    const k = 1 - Math.exp(-dt / (storm ? AMBIENCE.raftTau : AMBIENCE.tau));
    for (const s of SLOTS) this.levels[s] += (targets[s] - this.levels[s]) * k;
    this.mountain += (mountainWind(i) - this.mountain) * k;
    for (const id of BED_IDS) {
      if (targets[id] > AMBIENCE.request || (this.requested.has(id) && targets[id] > 0)) this.requested.set(id, undefined);
      else if (this.requested.has(id)) {
        const since = this.requested.get(id) ?? now;
        this.requested.set(id, since);
        if (now - since >= AMBIENCE.releaseMs && this.levels[id] < AMBIENCE.silent) this.requested.delete(id);
      }
    }
    const score = (id: BedId) => Math.max(targets[id], this.levels[id]) + (this.playing.includes(id) ? AMBIENCE.stickiness : 0);
    this.playing = [...this.requested.keys()].sort((a, b) => score(b) - score(a) || BED_IDS.indexOf(a) - BED_IDS.indexOf(b)).slice(0, AMBIENCE.maxPlaying);
    return {
      input: { ...i },
      targets,
      levels: { ...this.levels },
      mountain: this.mountain,
      requested: [...this.requested.keys()],
      playing: [...this.playing],
      prefetch: prefetchBeds(i).filter((id) => !this.playing.includes(id)),
    };
  }
}

/** O vento sintetizado: abaixa a 30 % sob qualquer leito, dobra nas montanhas e sobe sem a tempestade gravada. */
export const WIND = { base: 0.12, duck: 0.7, mountain: 1, stormMissing: 1.5 } as const;

/** Quanto de cada camada sintetizada toca (0 a 1; o vento, o ganho dele). */
export interface SynthCover {
  /** Pássaros (de dia), grilos e sapos (de noite) e ondas. */
  birds: number;
  night: number;
  waves: number;
  wind: number;
}

/**
 * Cobertura da síntese pelos leitos que tocam de fato (`audible`; um leito que falta não cobre
 * nada e a camada volta): pássaros sob a floresta (a de dia e, na Boca da Noite e no amanhecer,
 * quando uma sai e a outra entra, as duas juntas), grilos e sapos sob a de noite, ondas sob a
 * praia ou o mar (o maior), e a tempestade cobre todas. O vento abaixa a 30 % sob qualquer leito,
 * dobra nas montanhas e, na travessia sem a tempestade gravada, faz as vezes dela.
 */
export function synthCover(levels: Weights, audible: Partial<Record<BedId, number>>, mountain: number): SynthCover {
  const a = (id: BedId) => clamp01(audible[id] ?? 0);
  const storm = a("mar-tempestade");
  const any = clamp01(BED_IDS.reduce((s, id) => s + a(id), 0));
  const stormMissing = Math.max(0, levels["mar-tempestade"] - storm);
  return {
    birds: 1 - Math.max(clamp01(a("floresta-dia") + a("floresta-noite")), storm),
    night: 1 - Math.max(a("floresta-noite"), storm),
    waves: 1 - Math.max(a("praia"), a("mar"), storm),
    wind: WIND.base * ((1 - WIND.duck * any) * (1 + WIND.mountain * clamp01(mountain)) + WIND.stormMissing * stormMissing),
  };
}
